import React, { useEffect } from 'react';
import styled from 'styled-components';

import { slideInFromLeftWeighted } from '../../shared/component/Animation';
import { BlackFitBtn, PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { paperTextureModal } from '../../shared/component/paperTexture';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getPageMessage } from '../../shared/constants/pageMessages';
import { AlertPosition, AlertType, ProjectElementType } from '../../shared/enum/EnumRepository';
import { useProjectDetailState } from '../../shared/hooks/useApi/useProjectDetail';
import { useProjectElementActions, useProjectElementState } from '../../shared/hooks/useApi/useProjectElement';
import { useCanShowProjectDetail } from '../../shared/hooks/useProjectVisibility';
import { useImportStationWorkStore } from '../../shared/store/importStationWorkStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useModalWorkTagStore } from '../../shared/store/workTagStore';
import { useModalPageStore } from '../../shared/store/pageStore';

import ImportTagStation from './ImportTagStation';
import PageAboutWorkImport from './PageAboutWorkImport';
import SelectedDataThumb from './SelectedDataThumb';
import SimpleWorkForPE from './SimpleWorkForPE';

const ImportStationWorks: React.FC = () => {
  const { isClosing } = useModalStore();
  const { selectedDataList, clearSelectedDataList, clearSimpleList } = useImportStationWorkStore();
  const { project } = useProjectDetailState();
  const canShowProject = useCanShowProjectDetail();

  const { clearModal } = useModalStore();
  const { simpleList } = useImportStationWorkStore();
  const { projectElementList } = useProjectElementState();
  const { importWorkOrDetailAsPeBulk } = useProjectElementActions();
  const { enqueueAlert } = useStandardAlertStore();
  const { selectedModalTags, clearSelectedModalTags } = useModalWorkTagStore();
  const resetModalPage = useModalPageStore((s) => s.resetModalPage);

  const clearImportModalState = () => {
    clearSelectedDataList();
    clearSimpleList();
    clearSelectedModalTags();
    resetModalPage();
  };

  useEffect(() => {
    return () => {
      clearImportModalState();
    };
  }, []);

  const handleSubmit = async () => {
    if (!project) return;
    if (selectedDataList.length === 0){
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_IMPORT_EMPTY'),
      });
      return;
    }
    const workIdsInProject = new Set(
      projectElementList
        .filter((pe) => pe.projectElementType === ProjectElementType.WORK)
        .map((pe) => (pe as { work: { id: string } }).work.id)
    );
    const detailIdsInProject = new Set(
      projectElementList
        .filter((pe) => pe.projectElementType === ProjectElementType.DETAIL)
        .map((pe) => (pe as { workDetail: { id: string } }).workDetail.id)
    );
    const workIdList = selectedDataList
      .filter((item) => item.detailId == null && !workIdsInProject.has(item.workId))
      .map((item) => item.workId);
    const workDetailIdList = selectedDataList
      .filter((item) => item.detailId != null && !detailIdsInProject.has(item.detailId!))
      .map((item) => item.detailId!);
    if (workIdList.length === 0 && workDetailIdList.length === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_IMPORT_EMPTY'),
      });
      return;
    }
    await importWorkOrDetailAsPeBulk({
      projectId: project.id,
      workIdList,
      workDetailIdList,
    });
  };

  const handleCancel = () => {
    clearImportModalState();
    clearModal();
  }

  if (!canShowProject || !project) return null;

  return (
    <LeftModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <ImportComp>
        <ImportMainTitleWrapper>
          <ImportTitle>{getModalMessage('SELECT_TO_IMPORT_WORK_DETAIL')}</ImportTitle>
          <ImportSubTitle />
        </ImportMainTitleWrapper>
        <SelectedDataContainer>
          {selectedDataList.length === 0 ? (
            <SelectedDataPlaceholder>
              {selectedModalTags.length > 0 && simpleList.length === 0
                ? getPageMessage('IMPORT_STATION_NO_WORK_FOR_SELECTED_TAGS')
                : getModalMessage('SELECT_TO_IMPORT_EMPTY')}
            </SelectedDataPlaceholder>
          ) : (
            selectedDataList.map((item, i) => (
              <SelectedDataThumb
                key={`${item.workId}-${item.detailId ?? 'work'}-${i}`}
                item={item}
              />
            ))
          )}
        </SelectedDataContainer>
        <ImportTagStation />
        <ImportContentContainer>
          {simpleList.length === 0 ? (
            <NoContentContainer>
              {selectedModalTags.length > 0
                ? getPageMessage('IMPORT_STATION_NO_WORK_FOR_SELECTED_TAGS')
                : getPageMessage('IMPORT_STATION_EMPTY_WORK')}
            </NoContentContainer>
          ) : (
            simpleList.map((sw) => (
              <SimpleContentComp key={sw.id}>
                <SimpleWorkForPE data={sw} />
              </SimpleContentComp>
            ))
          )}
        </ImportContentContainer>
        <PageAboutWorkImport />
      </ImportComp>
      <ButtonContainer>
          <HeadlessBtn
            value={getBtnName('IMPORT')}
            handleClick={handleSubmit}
            StyledBtn={BlackFitBtn}
          />
          <HeadlessBtn
            value={getBtnName('CANCEL')}
            handleClick={handleCancel}
            StyledBtn={PaperBlurFitBtn}
          />
        </ButtonContainer>
    </LeftModalFrame>
  );
};

export const LeftModalFrame = styled.div<{ $isClosing: boolean; }>`
  position: absolute;
  left: 4vw;
  width: calc(40vw + 40px);
  min-width: 500px;
  max-width: 600px;
  height: 88vh;

  padding: 20px;
  

  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) =>
    $isClosing
      ? 'translateX(-100%)'
      : 'translateX(0)'
  };

  transition: all 0.6s ease-out;
  ${paperTextureModal}
  ${slideInFromLeftWeighted}
`;

const ImportComp = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;


const ImportMainTitleWrapper = styled.div`
  width: 100%;
  height: 5vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const ImportTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const ImportSubTitle = styled.div`
  margin-top: 6px;
  text-decoration: underline;
  font-style: italic;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.color_Gray_01};
  ${({ theme }) => theme.typography.Body_04};
`;

const SelectedDataContainer = styled.div`
  height: 10vh;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  padding: 10px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SelectedDataPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const ImportContentContainer = styled.div`
  width: 100%;
  height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NoContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_03_2};
  white-space: pre-line;
`;

const SimpleContentComp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ButtonContainer = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;



export default ImportStationWorks;
